/**
 * Tab switching for sections/product-recommendation-tabs.liquid ("You may also like").
 *
 * Plain WAI-ARIA tabs: [role="tab"] buttons control [role="tabpanel"] elements by
 * aria-controls; the inactive panels are `inert` (the CSS makes them invisible). Arrow keys / Home / End move between tabs. Each collection tab has a
 * matching "View all" link (same data-index) that shows only while that tab is active.
 *
 * The first panel wraps Horizon's <product-recommendations>, filled after paint. When Shopify
 * has no recommendations it marks itself `data-error` (product-recommendations.js); the
 * Suggested tab then drops out and the next tab takes over. `data-tab-count` on the host
 * lets the CSS hide the tab row (1 tab) or the whole section (0 tabs).
 *
 * "View more" (desktop): `data-rows` on the host says how many rows of cards show; the
 * section CSS hides the rest. Each press adds a row to every panel at once (so the shared
 * panel cell grows evenly). When the active panel has nothing left, the button is swapped
 * for that tab's "View all" link, or removed on Suggested. Switching tab goes back to the
 * first row(s). Revealed cards fade in (CSS, `.recommendation-tabs__reveal`).
 */
class RecommendationTabs extends HTMLElement {
  /** @type {MutationObserver | null} */
  #observer = null;

  /** Rows shown before any "View more" (the server-rendered `data-rows`). */
  #initialRows = 0;

  connectedCallback() {
    this.#initialRows = Number(this.dataset.rows) || 0;
    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeydown);

    const suggested = this.querySelector('product-recommendations');
    if (suggested) {
      // childList: the recommendations arrive after paint, which changes how many cards
      // Suggested has and so whether "View more" still has anything to add.
      this.#observer = new MutationObserver(() => {
        this.#syncSuggested(suggested);
        this.#syncMore();
      });
      this.#observer.observe(suggested, { attributes: true, attributeFilter: ['data-error'], childList: true });
      this.#syncSuggested(suggested);
    }

    this.#updateCount();
    this.#syncMore();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('keydown', this.#onKeydown);
    this.#observer?.disconnect();
  }

  /** @returns {HTMLElement | null} */
  get #activePanel() {
    const activeTab = this.#tabs.find((tab) => tab.getAttribute('aria-selected') === 'true');
    const panelId = activeTab?.getAttribute('aria-controls');
    return panelId ? document.getElementById(panelId) : null;
  }

  /** @param {HTMLElement | null} panel @returns {HTMLElement[]} */
  #cards(panel) {
    return panel
      ? /** @type {HTMLElement[]} */ (Array.from(panel.querySelectorAll('.resource-list--grid > .resource-list__item')))
      : [];
  }

  get #columns() {
    return Number(this.dataset.columns) || 1;
  }


  /** @returns {HTMLButtonElement[]} */
  get #tabs() {
    return /** @type {HTMLButtonElement[]} */ (Array.from(this.querySelectorAll('[role="tab"]')));
  }

  /** @returns {HTMLButtonElement[]} */
  get #visibleTabs() {
    return this.#tabs.filter((tab) => !tab.hidden);
  }

  /** @param {Event} event */
  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (target.closest('.recommendation-tabs__more-button')) {
      // detail 0 = keyboard (Enter/Space); focus then moves to the first new card.
      this.#showMore(/** @type {MouseEvent} */ (event).detail === 0);
      return;
    }
    const tab = target.closest('[role="tab"]');
    if (tab instanceof HTMLButtonElement && this.contains(tab)) this.#select(tab);
  };

  /** @param {boolean} [fromKeyboard] */
  #showMore(fromKeyboard = false) {
    const rows = Number(this.dataset.rows);
    if (!rows) return;
    const start = rows * this.#columns;
    this.dataset.rows = String(rows + 1);

    const revealed = this.#cards(this.#activePanel).slice(start, start + this.#columns);
    for (const card of revealed) {
      card.classList.add('recommendation-tabs__reveal');
      /** @param {AnimationEvent} animationEvent */
      const done = (animationEvent) => {
        if (animationEvent.target !== card) return; // ignore animations inside the card
        card.classList.remove('recommendation-tabs__reveal');
        card.removeEventListener('animationend', done);
      };
      card.addEventListener('animationend', done);
    }
    this.#syncMore();
    if (fromKeyboard) revealed[0]?.querySelector('a')?.focus({ preventScroll: true });
  }

  /** Button while the active panel has hidden cards; then "View all" or nothing. */
  #syncMore() {
    const button = this.querySelector('.recommendation-tabs__more-button');
    const endLink = this.querySelector('.recommendation-tabs__more-link');
    if (!(button instanceof HTMLButtonElement) || !(endLink instanceof HTMLAnchorElement)) return;

    const activeTab = this.#tabs.find((tab) => tab.getAttribute('aria-selected') === 'true');
    const cards = this.#cards(this.#activePanel).length;
    const shown = Number(this.dataset.rows) * this.#columns;
    const hasMore = cards > shown;

    // Keep focus on the control when the button gives way to the link.
    const hadFocus = document.activeElement === button;
    button.hidden = !hasMore;

    const viewAll = this.querySelector(`.recommendation-tabs__view-all[data-index="${activeTab?.dataset.index}"]`);
    if (!hasMore && viewAll instanceof HTMLAnchorElement) {
      endLink.href = viewAll.href;
      endLink.textContent = viewAll.textContent?.trim() ?? '';
      endLink.hidden = false;
      if (hadFocus) endLink.focus({ preventScroll: true });
    } else {
      endLink.hidden = true;
    }
  }

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    const current = /** @type {HTMLElement} */ (event.target).closest('[role="tab"]');
    if (!(current instanceof HTMLButtonElement)) return;

    const tabs = this.#visibleTabs;
    const index = tabs.indexOf(current);
    /** @type {HTMLButtonElement | undefined} */
    let next;
    switch (event.key) {
      case 'ArrowRight':
        next = tabs[(index + 1) % tabs.length];
        break;
      case 'ArrowLeft':
        next = tabs[(index - 1 + tabs.length) % tabs.length];
        break;
      case 'Home':
        next = tabs[0];
        break;
      case 'End':
        next = tabs[tabs.length - 1];
        break;
      default:
        return;
    }
    event.preventDefault();
    if (next) {
      this.#select(next);
      next.focus({ preventScroll: true });
    }
  };

  /** @param {HTMLButtonElement} selected */
  #select(selected) {
    for (const tab of this.#tabs) {
      const isSelected = tab === selected;
      tab.setAttribute('aria-selected', String(isSelected));
      tab.tabIndex = isSelected ? 0 : -1;

      const panelId = tab.getAttribute('aria-controls');
      const panel = panelId ? document.getElementById(panelId) : null;
      // `inert`, not `hidden`: panels share one grid cell (see the section CSS), so the
      // section is always as tall as the tallest panel and a tab change never moves the page.
      if (panel) panel.inert = !isSelected;
    }

    const index = selected.dataset.index;
    for (const link of this.querySelectorAll('.recommendation-tabs__view-all')) {
      if (link instanceof HTMLElement) link.hidden = link.dataset.index !== index;
    }

    // Every tab starts from its first row(s), not from where the last one was left.
    if (this.#initialRows) this.dataset.rows = String(this.#initialRows);
    this.#syncMore();
  }

  /**
   * Hide the Suggested tab when Shopify returned nothing, and hand over to the next tab.
   * @param {HTMLElement} suggested
   */
  #syncSuggested(suggested) {
    if (!suggested.dataset.error) return;

    // Toggle the wrapping panel, never <product-recommendations> itself: its own observer
    // reloads on any attribute change to it.
    const panel = suggested.closest('[role="tabpanel"]');
    const tab = this.#tabs.find((t) => t.getAttribute('aria-controls') === panel?.id);
    if (!panel || !tab || tab.hidden) return;

    const wasSelected = tab.getAttribute('aria-selected') === 'true';
    tab.hidden = true;
    panel.hidden = true;
    const first = this.#visibleTabs[0];
    if (wasSelected && first) this.#select(first);
    this.#updateCount();
  }

  #updateCount() {
    this.dataset.tabCount = String(this.#visibleTabs.length);
  }
}

if (!customElements.get('recommendation-tabs')) {
  customElements.define('recommendation-tabs', RecommendationTabs);
}
